import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AJHMLogo from './AJHMLogo';
import { getMenuItems, subscribeToMenuItems, DEFAULT_MENU_ITEMS } from '../services/menuService';
import { usePageSectionMenus } from './RawHtmlPage';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [navLinks, setNavLinks] = useState(DEFAULT_MENU_ITEMS);
  const location = useLocation();
  const pageSectionMenus = usePageSectionMenus();
  const dropdownTimerRef = useRef(null);
  const navScrollRef = useRef(null);

  // Check if navbar can scroll left or right
  const checkScroll = () => {
    const el = navScrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 2;
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(hasOverflow && el.scrollLeft < (el.scrollWidth - el.clientWidth - 6));
  };

  useEffect(() => {
    let isMounted = true;
    
    // 1. Initial fetch from public/data/menu.json
    getMenuItems().then((items) => {
      if (isMounted && Array.isArray(items) && items.length > 0) {
        setNavLinks(items);
      }
    });

    // 2. Real-time subscription: when an admin toggles active/inactive, update IMMEDIATELY
    const unsubscribe = subscribeToMenuItems((items) => {
      if (isMounted && Array.isArray(items) && items.length > 0) {
        setNavLinks(items);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
      if (dropdownTimerRef.current) {
        clearTimeout(dropdownTimerRef.current);
      }
    };
  }, []);

  // Handle horizontal scrolling via mouse wheel and update scroll indicators
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;

    checkScroll();

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const hasOverflow = el.scrollWidth > el.clientWidth;
        if (!hasOverflow) return;

        const canLeft = el.scrollLeft > 0;
        const canRight = el.scrollLeft < (el.scrollWidth - el.clientWidth - 1);
        if ((e.deltaY < 0 && canLeft) || (e.deltaY > 0 && canRight)) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
          checkScroll();
        }
      }
    };

    const handleWindowResize = () => {
      checkScroll();
      setActiveDropdown(null);
    };

    const handleWindowScroll = () => {
      setActiveDropdown(null);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => {
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [navLinks]);

  // Handle hash scrolling with proper header offset so content is never covered
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  const handleMouseEnter = (itemId, e) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
      dropdownTimerRef.current = null;
    }
    if (e && e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropdownWidth = 320;
      const spaceRight = window.innerWidth - rect.left;
      const leftPos = spaceRight < dropdownWidth ? Math.max(10, rect.right - dropdownWidth) : rect.left;
      setDropdownPos({
        top: rect.bottom + 4,
        left: leftPos,
      });
    }
    setActiveDropdown(itemId);
  };

  const handleMouseLeave = () => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
    }
    // 250ms grace period so moving cursor to dropdown never abruptly closes it
    dropdownTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 250);
  };

  const handleNavScroll = () => {
    checkScroll();
    if (activeDropdown) {
      setActiveDropdown(null);
    }
  };

  const scrollNav = (direction) => {
    const el = navScrollRef.current;
    if (!el) return;
    const distance = 200;
    el.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth'
    });
    setTimeout(checkScroll, 250);
  };

  const toggleMobileSubmenu = (id) => {
    setOpenMobileSubmenu((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleLinkClick = () => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
      dropdownTimerRef.current = null;
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  // Helper to remove any section numbers (e.g., "2.1 ", "2.2.1 ", "1. ")
  const cleanLabel = (text) => {
    if (!text) return '';
    return text.replace(/^\d+(\.\d+)*\.?\s*/, '').trim();
  };

  // Only display active navigation links & active submenus.
  // Pages in Simple HTML mode get their submenu from the headings (with ids) written in their HTML.
  const visibleNavLinks = navLinks
    .filter((item) => item.isActive !== false)
    .map((item) => {
      const sectionMenu = pageSectionMenus[(item.path || '').split('#')[0]];
      return {
        ...item,
        children: sectionMenu
          ? sectionMenu
          : Array.isArray(item.children)
            ? item.children.filter((child) => child.isActive !== false)
            : []
      };
    });

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm transition-all w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between min-h-[5rem] lg:min-h-[5.5rem] py-1 gap-2 xl:gap-3 w-full">
          
          {/* Left: Official Vectorized Logo & Title */}
          <Link to="/" className="flex items-center flex-shrink-0 group py-1" onClick={handleLinkClick}>
            <AJHMLogo height={58} className="flex-shrink-0" />
          </Link>

          {/* Center: Horizontally Scrollable Desktop Navigation Menu Links */}
          <div className="hidden lg:flex items-center flex-1 min-w-0 mx-2 xl:mx-3 relative">
            {/* Left Scroll Indicator Arrow */}
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => scrollNav('left')}
                className="absolute left-0 z-20 p-1.5 rounded-full bg-white/95 shadow-md border border-gray-200 text-gray-700 hover:text-[#03478E] hover:bg-white transition-all -ml-2"
                aria-label="Scroll navigation left"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Scrollable Nav Track */}
            <div
              ref={navScrollRef}
              onScroll={handleNavScroll}
              className="flex items-center overflow-x-auto no-scrollbar scroll-smooth w-full py-2 px-1"
            >
              <nav className="flex items-center space-x-0.5 xl:space-x-1 text-xs xl:text-sm font-semibold text-gray-700 whitespace-nowrap min-w-max">
                {visibleNavLinks.map((item, idx) => {
                  const basePath = item.path ? item.path.split('#')[0] : '';
                  const isActive = location.pathname === basePath;
                  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

                  return (
                    <div
                      key={item.id || item.label}
                      className="relative flex items-center h-full"
                      onMouseEnter={(e) => hasChildren && handleMouseEnter(item.id || item.label, e)}
                      onMouseLeave={() => hasChildren && handleMouseLeave()}
                    >
                      <Link
                        to={item.path}
                        onClick={handleLinkClick}
                        className={`inline-flex items-center gap-1 px-2 xl:px-2.5 py-1.5 rounded-full transition-colors duration-150 whitespace-nowrap hover:text-[#03478E] ${
                          isActive ? 'text-[#03478E] font-bold bg-blue-100/60' : 'hover:bg-blue-50/60'
                        }`}
                      >
                        <span>{cleanLabel(item.label)}</span>
                        {hasChildren && (
                          <svg
                            className={`w-3 h-3 transition-transform duration-200 ${
                              activeDropdown === (item.id || item.label) ? 'rotate-180 text-[#03478E]' : 'text-gray-400'
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </Link>

                      {idx < visibleNavLinks.length - 1 && (
                        <span className="text-gray-200 font-light select-none mx-0.5 xl:mx-1">|</span>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>

            {/* Right Scroll Indicator Arrow */}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => scrollNav('right')}
                className="absolute right-0 z-20 p-1.5 rounded-full bg-white/95 shadow-md border border-gray-200 text-gray-700 hover:text-[#03478E] hover:bg-white transition-all -mr-2"
                aria-label="Scroll navigation right"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>

          {/* Active Dropdown Menu (Fixed positioned so it is never clipped by horizontal overflow) */}
          {activeDropdown && (() => {
            const activeItem = visibleNavLinks.find((i) => (i.id || i.label) === activeDropdown);
            if (!activeItem || !activeItem.children || activeItem.children.length === 0) return null;

            return (
              <div 
                className="fixed min-w-[270px] max-w-sm sm:max-w-md w-max z-[100] animate-fadein"
                style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px` }}
                onMouseEnter={() => {
                  if (dropdownTimerRef.current) {
                    clearTimeout(dropdownTimerRef.current);
                    dropdownTimerRef.current = null;
                  }
                }}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 py-2.5 overflow-hidden whitespace-normal">
                  <div className="text-xs font-extrabold tracking-wider uppercase text-gray-400 px-4 py-1.5 border-b border-gray-50 mb-1">
                    {cleanLabel(activeItem.label)}
                  </div>
                  {activeItem.children.map((child) => (
                    <Link
                      key={child.id || child.label}
                      to={child.path}
                      onClick={handleLinkClick}
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-[#03478E] hover:bg-blue-50/80 transition-colors whitespace-normal break-words leading-relaxed"
                    >
                      {cleanLabel(child.label)}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Right: Submit Manuscript Button (Desktop) & Mobile Hamburger Controls */}
          <div className="flex items-center gap-2 xl:gap-3 flex-shrink-0">
            <Link
              to="/upload"
              onClick={handleLinkClick}
              className="hidden lg:inline-flex items-center text-xs xl:text-sm font-bold text-white bg-[#03478E] hover:bg-[#023366] hover:text-white px-4 xl:px-5 py-2 xl:py-2.5 rounded-full transition-all duration-200 uppercase tracking-wider whitespace-nowrap shadow-xs hover:shadow-md transform hover:-translate-y-0.5"
            >
              Submission
            </Link>

            {/* Mobile Controls */}
            <div className="flex lg:hidden items-center gap-2">
              <Link
                to="/upload"
                onClick={handleLinkClick}
                className="text-xs font-bold text-white bg-[#03478E] hover:text-white px-3.5 py-1.5 rounded-full shadow-xs"
              >
                Submit
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700 hover:text-[#03478E] focus:outline-none rounded-full"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile/Tablet Horizontally Scrollable Quick Nav Bar */}
      <div className="lg:hidden border-t border-gray-100 bg-[#FAFBFD] overflow-x-auto no-scrollbar px-3 py-2">
        <div className="flex items-center space-x-1.5 whitespace-nowrap min-w-max text-xs font-semibold text-gray-700">
          {visibleNavLinks.map((item) => {
            const basePath = item.path ? item.path.split('#')[0] : '';
            const isActive = location.pathname === basePath;
            return (
              <Link
                key={item.id || item.label}
                to={item.path}
                onClick={handleLinkClick}
                className={`px-3 py-1 rounded-full transition-colors ${
                  isActive
                    ? 'bg-[#03478E] text-white font-bold shadow-xs'
                    : 'bg-white border border-gray-200/80 text-gray-700 hover:bg-blue-50 hover:text-[#03478E]'
                }`}
              >
                {cleanLabel(item.label)}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white px-4 pt-3 pb-6 shadow-xl animate-fadein max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col space-y-1.5 text-sm font-semibold text-gray-700">
            {visibleNavLinks.map((item) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const isSubOpen = !!openMobileSubmenu[item.id || item.label];

              return (
                <div key={item.id || item.label} className="border-b border-gray-50 last:border-b-0 pb-1">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.path}
                      onClick={handleLinkClick}
                      className="py-2.5 px-3 rounded-xl hover:bg-blue-50 hover:text-[#03478E] transition flex-1 text-sm font-bold uppercase tracking-wider whitespace-normal break-words"
                    >
                      {cleanLabel(item.label)}
                    </Link>
                    {hasChildren && (
                      <button
                        type="button"
                        onClick={() => toggleMobileSubmenu(item.id || item.label)}
                        className="p-2 text-gray-400 hover:text-[#03478E] focus:outline-none"
                        aria-label="Toggle submenu"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${isSubOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Submenu Accordion */}
                  {hasChildren && isSubOpen && (
                    <div className="pl-4 pr-2 py-1 space-y-1 bg-gray-50/60 rounded-xl mb-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.id || child.label}
                          to={child.path}
                          onClick={handleLinkClick}
                          className="block py-2 px-3 rounded-lg text-sm font-medium text-gray-600 hover:text-[#03478E] hover:bg-white transition whitespace-normal break-words leading-relaxed"
                        >
                          {cleanLabel(child.label)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-3">
              <Link
                to="/upload"
                onClick={handleLinkClick}
                className="block text-center text-sm font-bold text-white bg-[#03478E] hover:bg-[#023366] py-3 rounded-full shadow-sm uppercase tracking-wider"
              >
                Submit Manuscript
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;